// Special Shader that just updates an existing material

import { Component, registerComponent, System, utils } from "aframe";
import { AdditiveBlending, BackSide, Color, DoubleSide, FrontSide, Material, Mesh, MultiplyBlending, NoBlending, Object3D, SubtractiveBlending, Texture, Vector2, Vector3, Vector4 } from "three";


type UpdateMaterialComponentData = { [key:string] : any } & { instance? : boolean };
interface UpdateMaterialComponent extends Component< UpdateMaterialComponentData >
{
	attrValue : any;
	material? : Material;
	originalMaterial? : Material | Material[];
	materialSrcs? : any;
	updateSchema( data : UpdateMaterialComponentData ) : void;
}



const textureSchema : { [key:string] : { type : string, default? : any } } = {
	offset : { type : "vec2" },
	repeat : { type : "vec2", default : { x : 1 , y : 1 } },
	center : { type : "vec2" },
	rotation : { type : "number" },
	flipY : { type : "boolean" },
	wrapS : { type : "number", default : NaN },
	wrapT : { type : "number", default : NaN },
	mapping : { type : "number", default : NaN },
	minFilter : { type : "number", default : NaN },
	magFilter : { type : "number", default : NaN },
	generateMipmaps : { type : "boolean", default : true }
};

const addTextureSchema = (prefix : string, materialSchema : any) =>
{
	for(let tkey in textureSchema)
	{
		const tschema = textureSchema[tkey];
		// Global for material :
		materialSchema[tkey] = tschema;
		// Specific for texture : 
		materialSchema[prefix + tkey.slice(0,1).toUpperCase() + tkey.slice(1)] = tschema;
	}
};



const classTypes : { [type:string] : Function } = {
	"color" : Color,
	"vec2" : Vector2,
	"vec3" : Vector3,
	"vec4" : Vector4
};




const buildSchema = (material : Material) =>
{
	const schema : { [key:string] : { type : string, default? : any } } = {};

	

	for(let key in material)
	{
		const value = (material as any)[key];

		if(key == "map") // known as "src" in AFrame... meh :/
		{
			schema.src = { type : "map", default : value };
			addTextureSchema("texture", schema);
			continue;
		}
		
		if(key.endsWith("Map")) // "somethingMap"
		{
			schema[key] = { type : "map", default : value };
			const textureName = key.slice(0, key.length - 3) + "Texture";
			addTextureSchema(textureName, schema);
			continue;
		}

		
		const type = typeof value;
		if( type !== 'object')
		{
			schema[key] = { type, default : value };
			continue;
		}

		if(Array.isArray(value))
		{
			schema[key] = { type : "array", default : value };
			continue;
		}

		for(let ctype in classTypes)
		{
			if(value instanceof classTypes[ctype])
			{
				schema[key] = { type : ctype, default : value };
				break;
			}
		}
	}

	return schema;
};


const nameMatches = (name : string, materialId : string) =>
{
	const attributeCompatibleName = name.replaceAll( /([^a-zA-Z0-9---])/g, '_' ).toLowerCase();
	return materialId == attributeCompatibleName;
};

const findMaterial = (obj : Object3D & { material? : Material | Material[] }, materialId : string) : Material | undefined =>
{
	if(obj.material)
	{
		if(Array.isArray(obj.material))
		{
			for(let m of obj.material)
				if(nameMatches(m.name, materialId))
					return m;
		}
		else if(nameMatches(obj.material.name, materialId))
			return obj.material;
	}

	let mat : Material | undefined = undefined;
	for(let child of obj.children)
		if((mat = findMaterial(child, materialId)))
			return mat;
};

registerComponent("update-material", {

	//Allow any property to be added, until the schema changes
	schema : { 
		"instance" : { type : "boolean", default : false },
		"": {
			parse : (value : any) => (value instanceof String) ? String(value) : value, //unbox
			stringify : (value : any) => String(value)
		} 
	},

	//When used with an Id, looks inside object3dMap for a material named "Id"
	multiple : true,

	init(this : UpdateMaterialComponent) {
		this.el.addEventListener("object3dset", (event) => {

			if( !event.detail.type != !this.id ) return;

			const materialBefore = this.material;
			this.updateSchema!(this.data);
			if(materialBefore !== this.material)
				this.update({});
		} );
	},


	updateSchema(this : UpdateMaterialComponent, data : UpdateMaterialComponentData) {
		
		let elMaterial : Material & { sourceMaterial? : Material } | undefined = undefined;

		if(this.id) //Id mode : seach for material in object3dMap
		{
			const matName = this.id.toLowerCase();
			for(let key in this.el.object3DMap)
			{
				elMaterial = findMaterial(this.el.object3DMap[key], matName);
				if(elMaterial)
					break;
			}
		}
		else
		{
			const elMesh = this.el.object3D as Mesh;
			const elMeshMaterial = elMesh && elMesh.material;

			elMaterial = Array.isArray(elMeshMaterial) ? elMeshMaterial[0] : elMeshMaterial;

			if(data.instance && elMaterial)
			{
				if( (elMaterial === this.material) && elMaterial.sourceMaterial )
					return;

				const instance : Material & { sourceMaterial? : Material } = elMaterial.clone();
				instance.sourceMaterial = elMaterial;
				elMaterial = instance;

				if(Array.isArray(elMeshMaterial))
					elMesh.material = [elMaterial, ...elMeshMaterial.slice(1) ];
				else
					elMesh.material = elMaterial;
			}
		}

		if(elMaterial === this.material)
			return;

		if(elMaterial)
		{
			this.material = elMaterial;
			this.extendSchema(buildSchema(elMaterial));
		}
		else
		{
			delete this.material;
			this.extendSchema( {} );
		}
	},

	update(this : UpdateMaterialComponent, oldData : any) {
		if(!(this.attrValue && this.material))
		{
			return;
		}

		const data = this.data;
		const rendererSystem = this.el.sceneEl!.systems.renderer as System & { applyColorCorrection(target : Color | Texture) : void };

		const texturesToUpdate : { [matName:string] : string } = {};

		for(let key in this.attrValue)
		{
			if(this.attrValue[key] === undefined)
			{
				//Don't really know why, but this happens...
				continue;
			}

			if(key == "src")
			{
				texturesToUpdate["map"] = key;
				continue;
			}
			if(key.endsWith("Map"))
			{
				texturesToUpdate[key] = key;
				continue;
			}

			if(!(key in this.material)) //This must be a texture sub-property
			{
				if( key.startsWith("texture") )
				{
					texturesToUpdate["map"] = "src";
					continue;
				}
				const ix = key.indexOf("Texture");
				if(ix >= 0)
				{
					const mapName = key.slice(0, ix) + "Map";
					texturesToUpdate[mapName] = mapName;
					continue;
				}
				continue;
			}
			
			const value = data[key], target = (this.material as any)[key];
			if( target && (typeof target === "object") )
			{
				if(target instanceof Color)
				{
					target.set(value);
					rendererSystem.applyColorCorrection(target);
				}
				else if(target.copy)
				{
					target.copy(value);
				}
				else if(target.set)
				{
					target.set(value);
				}
				else
					console.warn("Can't set new value for", key);
			}
			else if( key == "blending" )
			{
				if((value == "add") || (value == "additive"))
					this.material.blending = AdditiveBlending;
				else if((value == "subtract") || (value == "subtractive"))
					this.material.blending = SubtractiveBlending;
				else if(value == "multiply")
					this.material.blending = MultiplyBlending;
				else
				{
					const blendMode = parseInt(value);
					this.material.blending = isNaN(blendMode) ? NoBlending : blendMode;
				}
			}
			else if( key == "side" )
			{
				if(value == "back")
					this.material.side = BackSide;
				else if(value == "double")
					this.material.side = DoubleSide;
				else
					this.material.side = FrontSide;
			}
			else if( key == "shadowSide" )
			{
				if(!value)
					this.material.shadowSide = null;
				else if(value == "back")
					this.material.side = BackSide;
				else if(value == "double")
					this.material.side = DoubleSide;
				else
					this.material.side = FrontSide;
			}
			else
				(this.material as any)[key] = value;
		}


		for(let mapName in texturesToUpdate)
		{
			const dataName = texturesToUpdate[mapName];
			if( (dataName in this.attrValue) || (data[dataName] instanceof Texture) ) //Set new texture or change texture property
			utils.material.updateMapMaterialFromData(mapName, dataName, this, data);
		}

		this.material.needsUpdate = true;

	},

	remove(this : UpdateMaterialComponent)
	{
		//this.el.emit("restore-material", null, false);
	}

})